/**
 * Konva Room Canvas — Canvas Manager for AI Room Visualizer
 *
 * Replaces the DOM-based product overlay system with a Konva.js canvas.
 * Provides layered rendering: room image → zone guides → product overlays.
 * Handles drag, resize, rotate, snap-to-zone, perspective, and shadows.
 *
 * @requires Konva v9+ loaded from CDN
 */

(function() {
  'use strict';

  // ── Zone colour configuration ────────────────────────────────
  var ZONE_COLOURS = {
    wall:    { stroke: 'rgba(37, 99, 235, 0.6)',  fill: 'rgba(37, 99, 235, 0.08)',  highlight: 'rgba(37, 99, 235, 0.2)' },
    floor:   { stroke: 'rgba(34, 197, 94, 0.6)',   fill: 'rgba(34, 197, 94, 0.08)',   highlight: 'rgba(34, 197, 94, 0.2)' },
    surface: { stroke: 'rgba(245, 158, 11, 0.6)',  fill: 'rgba(245, 158, 11, 0.08)',  highlight: 'rgba(245, 158, 11, 0.2)' },
    ceiling: { stroke: 'rgba(168, 85, 247, 0.6)',  fill: 'rgba(168, 85, 247, 0.08)',  highlight: 'rgba(168, 85, 247, 0.2)' },
    // BS 7671 Bathroom Electrical Safety Zones
    zone_0:        { stroke: 'rgba(220, 38, 38, 0.8)',  fill: 'rgba(220, 38, 38, 0.15)',  highlight: 'rgba(220, 38, 38, 0.3)' },
    zone_1:        { stroke: 'rgba(245, 158, 11, 0.8)', fill: 'rgba(245, 158, 11, 0.15)', highlight: 'rgba(245, 158, 11, 0.3)' },
    zone_2:        { stroke: 'rgba(59, 130, 246, 0.8)', fill: 'rgba(59, 130, 246, 0.15)', highlight: 'rgba(59, 130, 246, 0.3)' },
    outside_zones: { stroke: 'rgba(107, 114, 128, 0.6)', fill: 'rgba(107, 114, 128, 0.08)', highlight: 'rgba(107, 114, 128, 0.2)' }
  };

  // Bathroom zone labels for canvas rendering
  var BATHROOM_ZONE_LABELS = {
    zone_0: 'Zone 0: Inside Bath/Shower (IPX7, SELV only)',
    zone_1: 'Zone 1: Above Bath/Shower (IPX4 min)',
    zone_2: 'Zone 2: Adjacent 0.6m (IPX4 min)',
    outside_zones: 'Outside Zones (IP20+, RCD required)'
  };

  var SNAP_THRESHOLD = 15; // percentage distance for snap-to-zone
  var ZONE_FADE_DELAY = 3000; // ms before zones auto-fade
  var ZONE_FADE_OPACITY = 0.2;

  // ── UK Electrical Standards (height from floor as % of room height) ──
  var UK_PLACEMENT_STANDARDS = {
    'switch':  { heightPct: 0.57 }, // ~1200mm from floor in 2.7m room
    'socket':  { heightPct: 0.92 }, // ~225mm from floor
    'dimmer':  { heightPct: 0.57 },
    'shaver':  { heightPct: 0.45 }, // ~1500mm from floor (en-suite)
    'usb-socket': { heightPct: 0.85 }
  };

  class KonvaRoomCanvas {
    /**
     * @param {string} containerId - ID of the container div
     * @param {Object} options
     * @param {Function} [options.onProductMoved] - Called when a product finishes dragging
     * @param {Function} [options.onProductResized] - Called when a product finishes resizing
     * @param {Function} [options.onProductSelected] - Called when a product is clicked
     * @param {Function} [options.onProductDeselected] - Called when canvas background is clicked
     * @param {Function} [options.onExternalDrop] - Called when a product is dropped from external grid
     */
    constructor(containerId, options) {
      this._containerId = containerId;
      this._options = options || {};
      this._products = {}; // productId → { node, meta }
      this._zones = [];
      this._selectedProductId = null;
      this._transformer = null;
      this._roomImageObj = null;
      this._stageWidth = 0;
      this._stageHeight = 0;
      this._roomAnalysis = null;
      this._zoneFadeTimer = null;

      this._initStage();
    }

    // ── Initialisation ─────────────────────────────────────────
    _initStage() {
      var container = document.getElementById(this._containerId);
      if (!container) {
        console.warn('KonvaRoomCanvas: container #' + this._containerId + ' not found');
        return;
      }

      // Use container width, set a reasonable default height (will resize with image)
      var w = container.offsetWidth || 800;
      var h = Math.round(w * 0.667); // 3:2 default aspect ratio

      this._stage = new Konva.Stage({
        container: this._containerId,
        width: w,
        height: h
      });

      // Layer 1: Background (room image)
      this._bgLayer = new Konva.Layer({ listening: false });
      this._stage.add(this._bgLayer);

      // Layer 2: Zone guides
      this._zoneLayer = new Konva.Layer();
      this._stage.add(this._zoneLayer);

      // Layer 3: Products (interactive)
      this._productLayer = new Konva.Layer();
      this._stage.add(this._productLayer);

      // Layer 4: UI (snap indicators, hints)
      this._uiLayer = new Konva.Layer({ listening: false });
      this._stage.add(this._uiLayer);

      // Transformer for selected product (resize + rotate handles)
      this._transformer = new Konva.Transformer({
        nodes: [],
        keepRatio: true,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        rotateEnabled: true,
        rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
        rotationSnapTolerance: 10,
        borderStroke: '#2563eb',
        borderStrokeWidth: 2,
        anchorStroke: '#2563eb',
        anchorFill: '#fff',
        anchorSize: 10,
        anchorCornerRadius: 2,
        boundBoxFunc: function(oldBox, newBox) {
          // Minimum size constraint (5% of stage)
          var minSize = Math.max(20, w * 0.03);
          if (newBox.width < minSize || newBox.height < minSize) {
            return oldBox;
          }
          return newBox;
        }
      });
      this._productLayer.add(this._transformer);

      // Click on background → deselect all
      var self = this;
      this._stage.on('click tap', function(e) {
        if (e.target === self._stage || e.target.getLayer() === self._bgLayer) {
          self.deselectAll();
        }
      });

      // Keyboard support
      this._keyHandler = this._handleKeyDown.bind(this);
      container.setAttribute('tabindex', '0');
      container.addEventListener('keydown', this._keyHandler);

      // Resize observer for responsive canvas
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(function() {
          self._handleResize();
        });
        this._resizeObserver.observe(container);
      }

      // Set up external drop target (for dragging from product grid)
      this._setupExternalDrop(container);
    }

    // ── Room Image ─────────────────────────────────────────────

    /**
     * Set the room image as the canvas background.
     * Resizes the stage to match the image aspect ratio.
     * @param {string} imageUrl - URL of the room image
     * @returns {Promise<void>}
     */
    setRoomImage(imageUrl) {
      var self = this;
      return new Promise(function(resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          // Resize stage to match image aspect ratio
          var container = document.getElementById(self._containerId);
          var containerWidth = container ? container.offsetWidth : 800;
          var scale = containerWidth / img.naturalWidth;
          var stageWidth = containerWidth;
          var stageHeight = Math.round(img.naturalHeight * scale);

          self._stageWidth = stageWidth;
          self._stageHeight = stageHeight;
          self._stage.width(stageWidth);
          self._stage.height(stageHeight);

          // Clear existing room image
          self._bgLayer.destroyChildren();

          // Add room image
          self._roomImageObj = new Konva.Image({
            image: img,
            x: 0,
            y: 0,
            width: stageWidth,
            height: stageHeight,
            listening: false
          });
          self._bgLayer.add(self._roomImageObj);
          self._bgLayer.draw();

          resolve();
        };
        img.onerror = function() {
          reject(new Error('Failed to load room image: ' + imageUrl));
        };
        img.src = imageUrl;
      });
    }

    // ── Zone Management ────────────────────────────────────────

    /**
     * Render placement zone overlays.
     * @param {Array} zones - Array of { name, type, x, y, width, height, suitableCategories }
     *   Coordinates are in percentage (0-100).
     */
    setZones(zones) {
      var self = this;
      this._zoneLayer.destroyChildren();
      this._zones = [];

      if (!zones || !zones.length) return;

      zones.forEach(function(zone) {
        var colours = ZONE_COLOURS[zone.type] || ZONE_COLOURS.wall;

        // Convert percentage bounds to pixel coordinates
        var x = (zone.x / 100) * self._stageWidth;
        var y = (zone.y / 100) * self._stageHeight;
        var w = (zone.width / 100) * self._stageWidth;
        var h = (zone.height / 100) * self._stageHeight;

        var group = new Konva.Group({ x: x, y: y });

        // Zone rectangle
        var rect = new Konva.Rect({
          width: w,
          height: h,
          fill: colours.fill,
          stroke: colours.stroke,
          strokeWidth: 2,
          dash: [6, 4],
          cornerRadius: 4,
          opacity: 1
        });
        group.add(rect);

        // Zone label (use bathroom safety label if applicable)
        var labelText = BATHROOM_ZONE_LABELS[zone.type] || zone.name.replace(/_/g, ' ');
        var label = new Konva.Text({
          text: labelText,
          fontSize: Math.max(10, Math.min(14, w * 0.08)),
          fill: colours.stroke,
          padding: 4,
          x: 4,
          y: 4,
          opacity: 0.8
        });
        group.add(label);

        // Category badges (small text below label)
        if (zone.suitableCategories && zone.suitableCategories.length > 0) {
          var badges = new Konva.Text({
            text: zone.suitableCategories.slice(0, 3).join(' | '),
            fontSize: Math.max(8, Math.min(11, w * 0.06)),
            fill: colours.stroke,
            padding: 2,
            x: 4,
            y: label.height() + 8,
            opacity: 0.6
          });
          group.add(badges);
        }

        self._zoneLayer.add(group);
        self._zones.push({
          name: zone.name,
          type: zone.type,
          x: zone.x,
          y: zone.y,
          width: zone.width,
          height: zone.height,
          suitableCategories: zone.suitableCategories || [],
          group: group,
          rect: rect
        });
      });

      self._zoneLayer.draw();

      // Auto-fade zones after delay
      this._startZoneFade();
    }

    _startZoneFade() {
      var self = this;
      if (this._zoneFadeTimer) clearTimeout(this._zoneFadeTimer);
      this._zoneFadeTimer = setTimeout(function() {
        self._zones.forEach(function(z) {
          z.group.to({ opacity: ZONE_FADE_OPACITY, duration: 0.5 });
        });
      }, ZONE_FADE_DELAY);
    }

    showZones() {
      if (this._zoneFadeTimer) clearTimeout(this._zoneFadeTimer);
      this._zones.forEach(function(z) {
        z.group.to({ opacity: 1, duration: 0.2 });
      });
    }

    hideZones() {
      this._zones.forEach(function(z) {
        z.group.to({ opacity: ZONE_FADE_OPACITY, duration: 0.3 });
      });
    }

    highlightZone(zoneName) {
      this._zones.forEach(function(z) {
        if (z.name === zoneName) {
          var colours = ZONE_COLOURS[z.type] || ZONE_COLOURS.wall;
          z.rect.fill(colours.highlight);
          z.group.opacity(1);
        } else {
          var colours2 = ZONE_COLOURS[z.type] || ZONE_COLOURS.wall;
          z.rect.fill(colours2.fill);
        }
      });
      this._zoneLayer.draw();
    }

    clearZoneHighlights() {
      this._zones.forEach(function(z) {
        var colours = ZONE_COLOURS[z.type] || ZONE_COLOURS.wall;
        z.rect.fill(colours.fill);
      });
      this._zoneLayer.draw();
    }

    // ── Product Placement ──────────────────────────────────────

    /**
     * Add a product image to the canvas.
     * @param {string} productId
     * @param {string} imageUrl - URL of the product image (cutout or regular)
     * @param {Object} bounds - { x, y, width } in percentage (0-100)
     * @param {Object} meta - { designProductId, zoneType, isCutout, scale, category }
     * @returns {Promise<Object>} The product state
     */
    addProduct(productId, imageUrl, bounds, meta) {
      var self = this;
      meta = meta || {};

      // Enforce product limit to prevent canvas performance degradation
      var MAX_PRODUCTS = 100;
      var productCount = Object.keys(this._products).length;
      if (productCount >= MAX_PRODUCTS && !this._products[productId]) {
        return Promise.reject(new Error('Maximum of ' + MAX_PRODUCTS + ' products reached. Remove some products before adding more.'));
      }

      // Remove existing product with same ID (replace)
      if (this._products[productId]) {
        this.removeProduct(productId);
      }

      return new Promise(function(resolve, reject) {
        if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') {
          reject(new Error('No valid image URL for product'));
          return;
        }

        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function() {
          bounds = bounds || {};
          var bx = (bounds.x !== undefined ? bounds.x : 30);
          var by = (bounds.y !== undefined ? bounds.y : 30);
          var bw = (bounds.width !== undefined ? bounds.width : 20);

          // Convert percentage to pixels
          var px = (bx / 100) * self._stageWidth;
          var py = (by / 100) * self._stageHeight;
          var pw = (bw / 100) * self._stageWidth;
          var ph = pw * (img.naturalHeight / img.naturalWidth); // maintain aspect ratio

          var productNode = new Konva.Image({
            image: img,
            x: px,
            y: py,
            width: pw,
            height: ph,
            draggable: true,
            name: 'product',
            id: 'product-' + productId
          });

          // Store metadata
          productNode.setAttr('productId', productId);
          productNode.setAttr('designProductId', meta.designProductId || null);
          productNode.setAttr('category', meta.category || '');
          productNode.setAttr('isCutout', meta.isCutout || false);
          productNode.setAttr('assetUrl', imageUrl);

          // Apply rendering hints (perspective, shadow, lighting)
          self._applyRenderingHints(productNode, {
            x: bx, y: by, width: bw
          }, meta.zoneType || 'wall');

          // ── Event handlers ──

          // Click to select
          productNode.on('click tap', function(e) {
            e.cancelBubble = true;
            self.selectProduct(productId);
          });

          // Drag start — show zone guides
          productNode.on('dragstart', function() {
            self.showZones();
          });

          // Drag move — highlight nearest zone
          productNode.on('dragmove', function() {
            var pos = self._getProductPercentPos(productNode);
            var nearest = self.findNearestZone(pos.x, pos.y);
            if (nearest) {
              self.highlightZone(nearest.zone.name);
            } else {
              self.clearZoneHighlights();
            }
          });

          // Drag end — snap to zone + save position
          productNode.on('dragend', function() {
            var pos = self._getProductPercentPos(productNode);
            var nearest = self.findNearestZone(pos.x, pos.y);

            if (nearest) {
              // Snap to zone
              var snapX = nearest.zone.x + nearest.zone.width * 0.2;
              var snapY = nearest.zone.y + nearest.zone.height * 0.2;

              // Apply UK electrical standards for switch/socket height
              var category = productNode.getAttr('category') || '';
              var standard = UK_PLACEMENT_STANDARDS[category];
              if (standard && nearest.zone.type === 'wall') {
                snapY = nearest.zone.y + nearest.zone.height * standard.heightPct;
              }

              var targetX = (snapX / 100) * self._stageWidth;
              var targetY = (snapY / 100) * self._stageHeight;

              productNode.to({
                x: targetX,
                y: targetY,
                duration: 0.2,
                easing: Konva.Easings.EaseOut,
                onFinish: function() {
                  self._applyRenderingHints(productNode, { x: snapX, y: snapY, width: bw }, nearest.zone.type);
                  self._productLayer.draw();
                }
              });
            }

            self.clearZoneHighlights();
            self.hideZones();

            // Notify parent
            var finalPos = self._getProductPercentPos(productNode);
            if (self._options.onProductMoved) {
              self._options.onProductMoved(productId, finalPos);
            }
          });

          // Transform end (resize/rotate)
          productNode.on('transformend', function() {
            var state = self.getProductState(productId);
            if (self._options.onProductResized) {
              self._options.onProductResized(productId, state);
            }
          });

          self._productLayer.add(productNode);

          // Animate fade-in
          productNode.opacity(0);
          productNode.to({ opacity: 1, duration: 0.3 });

          self._products[productId] = {
            node: productNode,
            meta: meta
          };

          self._productLayer.draw();

          resolve(self.getProductState(productId));
        };

        img.onerror = function() {
          reject(new Error('Failed to load product image: ' + imageUrl));
        };
        img.src = imageUrl;
      });
    }

    removeProduct(productId) {
      var entry = this._products[productId];
      if (!entry) return;

      // Remove from transformer
      if (this._selectedProductId === productId) {
        this._transformer.nodes([]);
        this._selectedProductId = null;
      }

      entry.node.destroy();
      delete this._products[productId];
      this._productLayer.draw();
    }

    selectProduct(productId) {
      var entry = this._products[productId];
      if (!entry) return;

      this._selectedProductId = productId;
      this._transformer.nodes([entry.node]);
      this._productLayer.draw();

      if (this._options.onProductSelected) {
        this._options.onProductSelected(productId);
      }
    }

    deselectAll() {
      this._selectedProductId = null;
      this._transformer.nodes([]);
      this._productLayer.draw();

      if (this._options.onProductDeselected) {
        this._options.onProductDeselected();
      }
    }

    bringForward(productId) {
      var entry = this._products[productId];
      if (!entry) return;
      entry.node.moveUp();
      this._productLayer.draw();
    }

    sendBackward(productId) {
      var entry = this._products[productId];
      if (!entry) return;
      entry.node.moveDown();
      this._productLayer.draw();
    }

    // ── Rendering Hints (Perspective, Shadows, Lighting) ───────

    /**
     * Compute and apply rendering hints to a product node.
     * Ports the existing _computeRenderingHints logic from ai-room-visualizer.js.
     */
    _applyRenderingHints(productNode, zoneBounds, zoneType) {
      if (!this._roomAnalysis) {
        // Apply basic shadow even without analysis
        productNode.shadowColor('rgba(0,0,0,0.15)');
        productNode.shadowBlur(6);
        productNode.shadowOffsetY(3);
        productNode.shadowOpacity(0.5);
        return;
      }

      var analysis = this._roomAnalysis;
      var lighting = analysis.lighting || 'natural';
      zoneType = zoneType || 'wall';
      zoneBounds = zoneBounds || { x: 30, y: 30, width: 20 };

      // Normalize to 0-1
      var bx = zoneBounds.x > 1 ? zoneBounds.x / 100 : zoneBounds.x;
      var by = zoneBounds.y > 1 ? zoneBounds.y / 100 : zoneBounds.y;

      // ── Shadow based on lighting ──
      var shadowBlur = 8;
      var shadowOpacity = 0.2;
      var shadowOffsetY = 4;
      var shadowOffsetX = 0;

      switch (lighting) {
        case 'bright':
          shadowOpacity = 0.25;
          shadowBlur = 10;
          shadowOffsetY = 5;
          break;
        case 'dim':
          shadowOpacity = 0.12;
          shadowBlur = 6;
          shadowOffsetY = 3;
          break;
        case 'natural':
          shadowOpacity = 0.2;
          shadowBlur = 8;
          shadowOffsetY = 4;
          break;
        case 'artificial':
          shadowOpacity = 0.18;
          shadowBlur = 7;
          shadowOffsetY = 4;
          break;
      }

      // Zone-based shadow direction
      if (zoneType === 'floor') {
        shadowOpacity *= 1.3;
        shadowBlur = Math.round(shadowBlur * 0.7);
        shadowOffsetY = Math.round(shadowOffsetY * 0.5);
      } else if (zoneType === 'wall') {
        shadowOffsetX = 2;
      } else if (zoneType === 'ceiling') {
        shadowOffsetY = -shadowOffsetY;
        shadowOpacity *= 0.7;
      }

      productNode.shadowColor('rgba(0,0,0,' + shadowOpacity.toFixed(2) + ')');
      productNode.shadowBlur(shadowBlur);
      productNode.shadowOffsetX(shadowOffsetX);
      productNode.shadowOffsetY(shadowOffsetY);
      productNode.shadowOpacity(1); // opacity baked into shadowColor

      // ── Perspective via skew ──
      var centerX = bx + (zoneBounds.width > 1 ? zoneBounds.width / 200 : zoneBounds.width / 2);
      var horizontalSkew = (centerX - 0.5) * -0.05; // subtle radians (equiv ~3deg max)

      if (Math.abs(horizontalSkew) > 0.005) {
        productNode.skewX(horizontalSkew);
      }

      // Depth scale: objects higher up (smaller by) appear smaller
      var depthScale = 0.85 + (by * 0.3); // 0.85-1.15
      if (Math.abs(depthScale - 1.0) > 0.03) {
        productNode.scaleX(productNode.scaleX() * depthScale);
        productNode.scaleY(productNode.scaleY() * depthScale);
      }
    }

    /**
     * Apply rendering hints from external analysis data.
     * @param {string} productId
     * @param {Object} hints - { shadowOpacity, shadowBlur, skewX, scale }
     */
    applyRenderingHints(productId, hints) {
      var entry = this._products[productId];
      if (!entry) return;

      if (hints.shadowOpacity !== undefined) entry.node.shadowOpacity(hints.shadowOpacity);
      if (hints.shadowBlur !== undefined) entry.node.shadowBlur(hints.shadowBlur);
      if (hints.skewX !== undefined) entry.node.skewX(hints.skewX);
      if (hints.scale !== undefined) {
        entry.node.scaleX(hints.scale);
        entry.node.scaleY(hints.scale);
      }
      this._productLayer.draw();
    }

    /**
     * Set room analysis data for rendering hints.
     */
    setRoomAnalysis(analysis) {
      this._roomAnalysis = analysis;
    }

    // ── Snap-to-Zone ───────────────────────────────────────────

    /**
     * Find the nearest zone to given percentage coordinates.
     * @param {number} x - X position in percentage (0-100)
     * @param {number} y - Y position in percentage (0-100)
     * @returns {{ zone: Object, distance: number } | null}
     */
    findNearestZone(x, y) {
      var nearest = null;
      var minDist = Infinity;

      this._zones.forEach(function(zone) {
        var cx = zone.x + zone.width / 2;
        var cy = zone.y + zone.height / 2;
        var dist = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(y - cy, 2));
        if (dist < minDist) {
          minDist = dist;
          nearest = zone;
        }
      });

      return nearest && minDist < SNAP_THRESHOLD ? { zone: nearest, distance: minDist } : null;
    }

    // ── Serialization ──────────────────────────────────────────

    /**
     * Export the canvas as a data URL (for before/after snapshots).
     * @param {Object} [options] - { pixelRatio, includeProducts }
     * @returns {string} Data URL
     */
    toDataURL(options) {
      options = options || {};
      var pixelRatio = options.pixelRatio || 1;

      if (options.includeProducts === false) {
        // Export room image only (before)
        this._productLayer.visible(false);
        this._zoneLayer.visible(false);
        this._uiLayer.visible(false);
        var dataUrl = this._stage.toDataURL({ pixelRatio: pixelRatio });
        this._productLayer.visible(true);
        this._zoneLayer.visible(true);
        this._uiLayer.visible(true);
        return dataUrl;
      }

      // Export full stage (after)
      this._zoneLayer.visible(false);
      this._uiLayer.visible(false);
      // Hide transformer handles for clean export
      this._transformer.visible(false);
      var dataUrl2 = this._stage.toDataURL({ pixelRatio: pixelRatio });
      this._zoneLayer.visible(true);
      this._uiLayer.visible(true);
      this._transformer.visible(true);
      return dataUrl2;
    }

    /**
     * Serialize canvas state for design save/restore.
     * @returns {Object} Serialized state
     */
    toJSON() {
      var products = {};
      var self = this;
      Object.keys(this._products).forEach(function(id) {
        var entry = self._products[id];
        var state = self.getProductState(id);
        products[id] = {
          state: state,
          meta: entry.meta,
          assetUrl: entry.node.getAttr('assetUrl')
        };
      });

      return {
        stageWidth: this._stageWidth,
        stageHeight: this._stageHeight,
        products: products
      };
    }

    /**
     * Restore canvas state from saved data.
     * @param {Object} data - Serialized state from toJSON()
     * @returns {Promise<void>}
     */
    async fromJSON(data) {
      if (!data || !data.products) return;

      var productKeys = Object.keys(data.products);
      for (var i = 0; i < productKeys.length; i++) {
        var id = productKeys[i];
        var p = data.products[id];
        if (p.assetUrl && p.state) {
          await this.addProduct(id, p.assetUrl, {
            x: p.state.x,
            y: p.state.y,
            width: p.state.width
          }, p.meta || {});
        }
      }
    }

    // ── Getters ────────────────────────────────────────────────

    getProductState(productId) {
      var entry = this._products[productId];
      if (!entry) return null;

      var node = entry.node;
      var pos = this._getProductPercentPos(node);

      return {
        x: pos.x,
        y: pos.y,
        width: (node.width() * node.scaleX() / this._stageWidth) * 100,
        height: (node.height() * node.scaleY() / this._stageHeight) * 100,
        rotation: node.rotation(),
        scale: node.scaleX()
      };
    }

    getAllProductStates() {
      var self = this;
      var states = [];
      Object.keys(this._products).forEach(function(id) {
        var state = self.getProductState(id);
        if (state) {
          state.productId = id;
          state.designProductId = self._products[id].node.getAttr('designProductId');
          states.push(state);
        }
      });
      return states;
    }

    getSelectedProductId() {
      return this._selectedProductId;
    }

    hasProducts() {
      return Object.keys(this._products).length > 0;
    }

    // ── External Drop (from product grid) ──────────────────────

    _setupExternalDrop(container) {
      var self = this;

      container.addEventListener('dragover', function(e) {
        try {
          var types = e.dataTransfer.types;
          if (types.indexOf('text/plain') === -1) return;
        } catch (err) { return; }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        self.showZones();
      });

      container.addEventListener('dragleave', function(e) {
        if (!container.contains(e.relatedTarget)) {
          self.hideZones();
          self.clearZoneHighlights();
        }
      });

      container.addEventListener('drop', function(e) {
        e.preventDefault();
        e.stopPropagation();
        self.hideZones();
        self.clearZoneHighlights();

        var raw = e.dataTransfer.getData('text/plain');
        if (!raw) return;

        var productData;
        try {
          productData = JSON.parse(raw);
        } catch (err) {
          return;
        }

        if (!productData.productId) return;

        // Calculate drop position as percentage
        var stageBox = self._stage.container().getBoundingClientRect();
        var dropX = ((e.clientX - stageBox.left) / stageBox.width) * 100;
        var dropY = ((e.clientY - stageBox.top) / stageBox.height) * 100;

        // Clamp
        dropX = Math.max(5, Math.min(dropX, 80));
        dropY = Math.max(5, Math.min(dropY, 80));

        if (self._options.onExternalDrop) {
          self._options.onExternalDrop(productData, { x: dropX, y: dropY });
        }
      });
    }

    // ── Keyboard ───────────────────────────────────────────────

    _handleKeyDown(e) {
      if (!this._selectedProductId) return;

      var entry = this._products[this._selectedProductId];
      if (!entry) return;

      var node = entry.node;
      var step = e.shiftKey ? 10 : 2; // pixels

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          node.x(Math.max(0, node.x() - step));
          this._productLayer.draw();
          break;
        case 'ArrowRight':
          e.preventDefault();
          node.x(Math.min(this._stageWidth - 20, node.x() + step));
          this._productLayer.draw();
          break;
        case 'ArrowUp':
          e.preventDefault();
          node.y(Math.max(0, node.y() - step));
          this._productLayer.draw();
          break;
        case 'ArrowDown':
          e.preventDefault();
          node.y(Math.min(this._stageHeight - 20, node.y() + step));
          this._productLayer.draw();
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (this._options.onProductRemoveRequested) {
            this._options.onProductRemoveRequested(this._selectedProductId);
          }
          break;
        case 'Escape':
          e.preventDefault();
          this.deselectAll();
          break;
      }
    }

    // ── Helpers ─────────────────────────────────────────────────

    _getProductPercentPos(node) {
      return {
        x: (node.x() / this._stageWidth) * 100,
        y: (node.y() / this._stageHeight) * 100
      };
    }

    _handleResize() {
      var container = document.getElementById(this._containerId);
      if (!container || !this._roomImageObj) return;

      var newWidth = container.offsetWidth;
      if (newWidth === this._stageWidth) return;

      var scaleRatio = newWidth / this._stageWidth;
      var newHeight = Math.round(this._stageHeight * scaleRatio);

      this._stageWidth = newWidth;
      this._stageHeight = newHeight;
      this._stage.width(newWidth);
      this._stage.height(newHeight);

      // Scale room image
      this._roomImageObj.width(newWidth);
      this._roomImageObj.height(newHeight);
      this._bgLayer.draw();

      // Scale all zones
      var self = this;
      this._zones.forEach(function(z) {
        var x = (z.x / 100) * newWidth;
        var y = (z.y / 100) * newHeight;
        var w = (z.width / 100) * newWidth;
        var h = (z.height / 100) * newHeight;
        z.group.x(x);
        z.group.y(y);
        z.rect.width(w);
        z.rect.height(h);
      });
      this._zoneLayer.draw();

      // Scale all products
      Object.keys(this._products).forEach(function(id) {
        var entry = self._products[id];
        var node = entry.node;
        node.x(node.x() * scaleRatio);
        node.y(node.y() * scaleRatio);
        node.width(node.width() * scaleRatio);
        node.height(node.height() * scaleRatio);
      });
      this._productLayer.draw();
    }

    /**
     * Clean up — remove event listeners and destroy Konva stage.
     */
    destroy() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      var container = document.getElementById(this._containerId);
      if (container && this._keyHandler) {
        container.removeEventListener('keydown', this._keyHandler);
      }
      if (this._zoneFadeTimer) {
        clearTimeout(this._zoneFadeTimer);
      }
      if (this._stage) {
        this._stage.destroy();
      }
    }
  }

  // Expose globally
  window.KonvaRoomCanvas = KonvaRoomCanvas;

})();
